import { Badge, Button, Flex, Input, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';

import {
	CertPoGroup,
	CertVendor,
	useGetCertPoGroupsQuery,
} from '../../redux/api/material-certs';
import { BUCKETS } from './buckets';
import SiteTag from './SiteTag';
import VendorTag from './VendorTag';

const { Text } = Typography;

/**
 * Every PO with certificate work, in a table that sorts and searches.
 *
 * The compact switcher in the queue is for hopping between the two or three
 * POs a reviewer is working through. This is the other job: finding the PO a
 * particular bar belongs to when all you have is a heat number off a
 * certificate, or working out which plant is waiting on the most paperwork.
 * Search goes to the server because heats, RT tags and EDPs are not in the
 * aggregated rows; sorting stays here because the list is short.
 */
export default function PoTable({
	selectedPo,
	onSelect,
}: {
	selectedPo: string | null;
	onSelect: (po: string) => void;
}) {
	const [search, setSearch] = useState('');
	const [query, setQuery] = useState('');
	const [showDone, setShowDone] = useState(false);

	const { data: groups = [], isFetching } = useGetCertPoGroupsQuery(
		query ? { search: query } : undefined
	);

	const rows = useMemo(
		() =>
			showDone ? groups : groups.filter((group) => group.pending_count > 0),
		[groups, showDone]
	);

	const columns: ColumnsType<CertPoGroup> = [
		{
			title: 'PO',
			dataIndex: 'po',
			width: 120,
			// Numeric compare: as strings, PO 900 would sort above PO 1000.
			sorter: (a, b) => Number(a.po) - Number(b.po) || a.po.localeCompare(b.po),
			render: (po: string) => (
				<Text strong={po === selectedPo}>PO {po}</Text>
			),
		},
		{
			title: 'Vendor',
			dataIndex: 'vendor_keys',
			width: 110,
			filters: [
				{ text: 'Alro', value: 'alro' },
				{ text: 'McMaster', value: 'mcmaster' },
			],
			onFilter: (value, row) =>
				(row.vendor_keys ?? []).includes(value as CertVendor),
			render: (vendors: CertVendor[]) => (
				<Flex gap={4} wrap>
					<VendorTag vendors={vendors} />
				</Flex>
			),
		},
		{
			title: 'Site',
			dataIndex: 'building_name',
			width: 130,
			sorter: (a, b) => a.building_name.localeCompare(b.building_name),
			filters: [
				{ text: 'West Bend', value: 'West Bend' },
				{ text: 'Cedarburg', value: 'Cedarburg' },
			],
			onFilter: (value, row) => row.building_name === value,
			render: (name: string) => <SiteTag name={name} compact />,
		},
		{
			title: 'Waiting',
			dataIndex: 'pending_count',
			width: 100,
			defaultSortOrder: 'descend',
			sorter: (a, b) => a.pending_count - b.pending_count,
			render: (count: number) =>
				count ? <Badge count={count} /> : <Tag color="default">Done</Tag>,
		},
		{
			title: 'State of play',
			key: 'state',
			render: (_, row) => (
				<Flex gap={4} wrap>
					{row.ready_count > 0 && (
						<Tag color={BUCKETS.ready.color}>{row.ready_count} ready</Tag>
					)}
					{row.waiting_cert_count > 0 && (
						<Tag color={BUCKETS.waiting_cert.color}>
							{row.waiting_cert_count} waiting
						</Tag>
					)}
					{row.orphan_cert_count > 0 && (
						<Tag color={BUCKETS.orphan_cert.color}>
							{row.orphan_cert_count} no slip
						</Tag>
					)}
					{row.supporting_document_count > 0 && (
						<Tooltip title={BUCKETS.supporting_document.hint}>
							<Tag color={BUCKETS.supporting_document.color}>
								{row.supporting_document_count} supporting
							</Tag>
						</Tooltip>
					)}
					{row.no_po_count > 0 && (
						<Tooltip title={BUCKETS.no_po.hint}>
							<Tag color={BUCKETS.no_po.color}>{row.no_po_count} no PO</Tag>
						</Tooltip>
					)}
					{row.confirmed_count > 0 && (
						<Tag color="blue">{row.confirmed_count} matched</Tag>
					)}
					{row.rejected_count > 0 && <Tag>{row.rejected_count} rejected</Tag>}
					{row.mixed_heat && (
						<Tooltip title="At least one packing-slip line carries more than one heat.">
							<Tag>mixed heats</Tag>
						</Tooltip>
					)}
				</Flex>
			),
		},
		{
			title: 'In queue since',
			dataIndex: 'oldest_received_at',
			width: 170,
			sorter: (a, b) =>
				dayjs(a.oldest_received_at ?? 0).valueOf() -
				dayjs(b.oldest_received_at ?? 0).valueOf(),
			render: (at: string | null) =>
				at ? (
					<Text type="secondary">{dayjs(at).format('MMM D, YYYY h:mm A')}</Text>
				) : (
					<Text type="secondary">—</Text>
				),
		},
		{
			title: '',
			key: 'open',
			width: 90,
			render: (_, row) => (
				<Button type="link" size="small" onClick={() => onSelect(row.po)}>
					Review
				</Button>
			),
		},
	];

	return (
		<Flex vertical gap={8} style={{ height: '100%' }}>
			<Flex justify="space-between" align="center" gap={8} wrap>
				<Space>
					<Input.Search
						allowClear
						placeholder="PO, heat, RT tag, EDP / SKU or vendor order"
						style={{ width: 340 }}
						value={search}
						onChange={(event) => {
							setSearch(event.target.value);
							if (!event.target.value) setQuery('');
						}}
						onSearch={(value) => setQuery(value.trim())}
						loading={isFetching}
					/>
					<Text type="secondary">
						{rows.length} PO{rows.length === 1 ? '' : 's'}
						{query && ' matching'}
					</Text>
				</Space>
				<Button onClick={() => setShowDone((on) => !on)}>
					{showDone ? 'Hide finished POs' : 'Show finished POs'}
				</Button>
			</Flex>
			<Table<CertPoGroup>
				size="small"
				rowKey="po"
				columns={columns}
				dataSource={rows}
				loading={isFetching && !groups.length}
				pagination={{ pageSize: 20, hideOnSinglePage: true }}
				onRow={(row) => ({
					onClick: () => onSelect(row.po),
					style: {
						cursor: 'pointer',
						background: row.po === selectedPo ? '#e6f4ff' : undefined,
					},
				})}
			/>
		</Flex>
	);
}
